import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useAdminGuard } from '../../hooks/useAdminGuard'
import { useSync } from '../../contexts/SyncContext'
import { useToast } from '../../hooks/useToast'
import { resetBranding } from '../../lib/branding'
import Footer from './Footer'
import CompanySwitcher from './CompanySwitcher'
import Wordmark from './Wordmark'
import ThemeToggle from './ThemeToggle'
import { useIsMobile } from '../../hooks/useIsMobile'
import MobileLayout from './mobile/MobileLayout'

interface Props {
  children: React.ReactNode
  title?: string
}

type NavItem =
  | { divider: true }
  | { to: string; label: string; icon: string; shortLabel?: string; adminOnly?: boolean }

const NAV_ITEMS: NavItem[] = [
  { to: '/',                     label: 'Painel K',              icon: '🚀' },
  { divider: true },
  { to: '/miguel-comprador',     label: 'Comprador',             icon: '✨' },
  { to: '/relatorio-compras',    label: 'Emissão de pedidos',    icon: '🧾' },
  { to: '/historico-pedidos',    label: 'Pedidos',               icon: '📦' },
  { divider: true },
  { to: '/abastecimento-filial', label: 'Abastecimento Filial',  icon: '🚚' },
  { to: '/cadastro-recebimento', label: 'Cadastro e Recebimento',icon: '📥' },
  { to: '/estoque-dashboard',    label: 'Dashboard',             icon: '🎯' },
  { to: '/custo-mercadoria',     label: 'Custo de Mercadoria',   icon: '🏷️', shortLabel: 'Custo Merc.' },
  { to: '/inativos',             label: 'Inativos',              icon: '🗑️' },
  { divider: true },
  { to: '/fornecedor',           label: 'Fornecedor',            icon: '🤝' },
  { to: '/configuracoes',        label: 'Configurações',         icon: '⚙️', adminOnly: true },
]

function relativeTime(iso: string | null): string {
  if (!iso) return 'nunca'
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `há ${h}h`
  const d = Math.floor(h / 24)
  return `há ${d}d`
}

export default function AppLayout({ children }: Props) {
  const isMobile = useIsMobile()
  const { user } = useAuth()
  const { isAdmin } = useAdminGuard()
  const {
    productsSyncing: syncing,
    productsLastSync: lastSync,
    productsResult: result,
    productsProgress: progress,
    startProductsSync,
    salesSyncing,
    salesProgress,
  } = useSync()
  const syncNow = () => { startProductsSync() }
  const { toast, notify, dismiss } = useToast()
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [cadastroRecAvailable, setCadastroRecAvailable] = useState<boolean>(false)
  const lastNotifiedResultRef = useRef<typeof result>(null)

  useEffect(() => {
    if (!user) return
    supabase
      .from('profiles')
      .select('company_id')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (!data?.company_id) return
        return supabase
          .from('companies')
          .select('logo_url, cadastro_recebimento_enabled, tiny_v3_connected_at')
          .eq('id', data.company_id)
          .single()
      })
      .then(res => {
        if (!res || !('data' in res) || !res.data) return
        if (res.data.logo_url) setLogoUrl(res.data.logo_url)
        // Recurso disponivel APENAS quando: flag enabled (default true)
        // E empresa NAO esta integrada ao Tiny v3. Mutual exclusion total.
        const enabled = res.data.cadastro_recebimento_enabled !== false
        const hasV3 = !!res.data.tiny_v3_connected_at
        setCadastroRecAvailable(enabled && !hasV3)
      })
  }, [user])

  useEffect(() => {
    if (!result) return
    // Evita re-notificar quando o usuário navega entre páginas (result vem do contexto e persiste)
    if (lastNotifiedResultRef.current === result) return
    lastNotifiedResultRef.current = result
    if (result.ok) {
      notify('success', `Sincronização: ${result.inserted ?? 0} produtos · ${result.stockUpdated ?? 0} estoques atualizados`)
    } else {
      notify('error', `Erro: ${result.error ?? 'desconhecido'}`)
    }
  }, [result, notify])

  const toastEl = toast && (
    <div className={`fixed top-2 sm:top-4 right-2 sm:right-4 left-2 sm:left-auto z-[60] px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg shadow-lg border text-sm font-medium ${
      toast.type === 'success'
        ? 'bg-brand-50 border-brand-200 text-brand-700 dark:text-brand-300 dark:bg-brand-500/15 dark:border-brand-500/30'
        : 'bg-danger/10 border-danger/30 text-danger'
    }`}>
      {toast.message}
      <button onClick={dismiss} className="ml-3 opacity-60 hover:opacity-100">×</button>
    </div>
  )

  // ── Caminho MOBILE ─────────────────────────────────────────────────────
  // Branch isolado: o JSX desktop abaixo permanece 100% inalterado.
  if (isMobile) {
    return (
      <>
        <MobileLayout
          logoUrl={logoUrl}
          isAdmin={isAdmin}
          cadastroRecAvailable={cadastroRecAvailable}
          userEmail={user?.email}
          sync={{ syncing, progress, onSync: syncNow }}
          salesSyncing={salesSyncing}
        >
          {children}
        </MobileLayout>
        {toastEl}
      </>
    )
  }

  // ── Caminho DESKTOP (inalterado) ───────────────────────────────────────
  return (
    <div className="h-screen bg-paper flex flex-col">
      <header className="shrink-0 sticky top-0 z-40 bg-[#0D1B2A] border-b border-white/10">
        <div className="px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-4 relative">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <img
              src={logoUrl ?? '/logo-ax.svg'}
              alt="Logo"
              className="h-9 sm:h-14 w-auto object-contain"
              onError={e => { (e.target as HTMLImageElement).src = '/logo-ax.svg' }}
            />
          </div>

          <span className="hidden md:block absolute left-1/2 -translate-x-1/2">
            <Wordmark showIcon={false} textClassName="text-2xl" onDark />
          </span>

          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <ThemeToggle onDark />
            {salesSyncing && (
              <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-sky-500/15 border border-sky-500/30 rounded-lg text-xs text-sky-300">
                <span className="w-3 h-3 border border-sky-400 border-t-transparent rounded-full animate-spin" />
                <span className="hidden sm:inline">
                  Vendas {salesProgress ? `· ${salesProgress.orders} ped / ${salesProgress.items} itens` : '...'}
                </span>
                <span className="sm:hidden">{salesProgress ? `${salesProgress.orders}p` : '...'}</span>
              </div>
            )}
            <button
              onClick={() => syncNow()}
              disabled={syncing}
              className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-white/10 hover:bg-white/20 disabled:opacity-50 rounded-lg text-xs text-white/90 transition-colors"
              title={`Última sincronização: ${relativeTime(lastSync)}`}
            >
              {syncing ? (
                <>
                  <span className="w-3 h-3 border border-white/60 border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">{progress ? `Sincronizando ${progress.done}/${progress.total}` : 'Sincronizando...'}</span>
                  <span className="sm:hidden">{progress ? `${progress.done}/${progress.total}` : '...'}</span>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Sincronizar ERP</span>
                  <span className="sm:hidden">Sync</span>
                  <span className="text-white/50">· {relativeTime(lastSync)}</span>
                </>
              )}
            </button>
            {user && <span className="text-xs text-white/50 hidden lg:block truncate max-w-[180px]">{user.email}</span>}
            <CompanySwitcher onDark />
            <button
              onClick={async () => { resetBranding(); await supabase.auth.signOut() }}
              className="text-xs text-white/70 hover:text-white transition-colors px-2 sm:px-3 py-1.5 rounded-lg hover:bg-white/10 shrink-0"
            >
              Sair
            </button>
          </div>
        </div>

        <nav className="px-2 sm:px-6 flex items-stretch gap-0.5 border-t border-white/10 overflow-x-auto">
          {NAV_ITEMS
            .filter(item => 'divider' in item || (!item.adminOnly || isAdmin))
            .filter(item => 'divider' in item || (item.to !== '/cadastro-recebimento' || cadastroRecAvailable))
            .map((item, i) => {
              if ('divider' in item) {
                return <div key={`div-${i}`} className="w-px bg-white/10 self-stretch my-1.5 mx-1 shrink-0" />
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  title={item.shortLabel ? item.label : undefined}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 text-sm px-3 sm:px-4 py-2 sm:py-3 border-b-2 transition-colors whitespace-nowrap shrink-0 rounded-t ${
                      isActive
                        ? 'border-brand-500 text-brand-500 bg-white/5'
                        : 'border-transparent text-slate-300 hover:text-white hover:bg-white/5'
                    }`
                  }
                >
                  <span>{item.icon}</span>
                  {item.shortLabel
                    ? <><span className="hidden lg:inline">{item.label}</span><span className="lg:hidden">{item.shortLabel}</span></>
                    : item.label
                  }
                </NavLink>
              )
            })}
        </nav>
      </header>

      <main className="flex-1 p-3 sm:p-6 overflow-auto">
        {children}
      </main>

      <Footer variant="dark" />

      {toast && (
        <div className={`fixed top-2 sm:top-4 right-2 sm:right-4 left-2 sm:left-auto z-50 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg shadow-lg border text-sm font-medium ${
          toast.type === 'success'
            ? 'bg-green-950 border-green-700 text-green-100'
            : 'bg-red-950 border-red-700 text-red-100'
        }`}>
          {toast.message}
          <button onClick={dismiss} className="ml-3 opacity-60 hover:opacity-100">×</button>
        </div>
      )}
    </div>
  )
}
